import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Scale } from "lucide-react";
import { FIRM } from "@/data/site";
import { LogoMark } from "@/components/Logo";

const STORAGE_KEY = "ha_bci_disclaimer_ack_v1";

export default function DisclaimerModal() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const ack = localStorage.getItem(STORAGE_KEY);
      if (!ack) setOpen(true);
    } catch {
      setOpen(true);
    }
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const agree = () => {
    try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch { /* ignore */ }
    setOpen(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
          data-testid="disclaimer-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="disclaimer-title"
        >
          {/* backdrop */}
          <div className="absolute inset-0 bg-[hsl(var(--navy-deep))]/80 backdrop-blur-md" />

          {/* panel */}
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl bg-[hsl(var(--ivory))] border hairline shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-7 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-[hsl(var(--navy))]">
                  <LogoMark className="h-11 w-11" />
                </span>
                <div>
                  <div className="serif text-xl leading-none">Helkar <span className="text-gold">&</span> Associates</div>
                  <div className="mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--muted-foreground))] mt-1">Est. 2017 Mumbai</div>
                </div>
              </div>

              <div className="mono text-xs uppercase tracking-[0.32em] text-gold mb-4">Disclaimer · Bar Council of India</div>
              <h2 id="disclaimer-title" className="serif text-3xl md:text-4xl leading-[1.02] mb-6">
                Please read before you <em className="italic text-gold">proceed.</em>
              </h2>

              <div className="space-y-4 text-[15px] leading-relaxed text-[hsl(var(--muted-foreground))]">
                <p>
                  As per the rules of the Bar Council of India, advocates are not permitted to solicit work or advertise.
                  By clicking “I Agree” below, the user acknowledges the following:
                </p>
                <ul className="space-y-3">
                  <li className="flex gap-3">
                    <span className="mono text-[10px] text-gold mt-1">01</span>
                    <span>There has been no advertisement, personal communication, solicitation, invitation or inducement of any sort whatsoever from Helkar &amp; Associates or any of its members to solicit any work through this website.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mono text-[10px] text-gold mt-1">02</span>
                    <span>The user wishes to gain more information about the firm for their own information and use.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mono text-[10px] text-gold mt-1">03</span>
                    <span>The information provided on this website is solely available at the user’s request for informational purposes only, and should not be interpreted as soliciting or advertising.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mono text-[10px] text-gold mt-1">04</span>
                    <span>None of the information contained on this website amounts to any form of legal opinion or legal advice. The firm is not liable for any consequence of any action taken by the user relying on material on this website.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="mono text-[10px] text-gold mt-1">05</span>
                    <span>Use of this website or submission of any form does not create an attorney–client relationship. Such a relationship arises only upon a formal written engagement.</span>
                  </li>
                </ul>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button onClick={agree} className="btn-ink flex-1 justify-center" data-testid="disclaimer-agree">
                  I Agree &amp; Enter
                </button>
                <a href={`mailto:${FIRM.email}`} className="btn-ghost flex-1 justify-center" data-testid="disclaimer-contact">
                  Contact Instead
                </a>
              </div>
              <p className="mt-5 text-xs text-[hsl(var(--muted-foreground))]">
                By proceeding you confirm that you have read and understood the disclaimer above.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
